import { useEffect } from "react";
import User from "../components/staff/User";
import { useButtonStore } from "../stores/components/button";
import { getUsers } from "../api/users";
import { showToastError } from "../services/toast";
import { useStaffStore } from "../stores/staff";
import TableStaff from "../components/ui/tables/TableStaff";
import { useUtilsStore } from "../stores/components/utils";
import Events from "../components/staff/Events";
import { getEvents } from "../api/events";
import { useLoaderStore } from "../stores/components/loader";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import useSorting from "../contexts/useSorting";
import { getSortButtonClasses, getSortIcon } from "../utils/sortIcons";
import {
  ActionButton,
  PageHeader,
  PagePanel,
  PageShell,
  PanelCenter,
  PanelToolbar,
  PlusIcon,
  ResultCount,
  SearchField,
  SortBar,
  TabBar,
  ToolbarRow,
} from "../components/ui/page/PageShell";

const Staff = () => {
  const { createButton, setCreateButton } = useButtonStore();
  const {
    users,
    setUsers,
    viewOrUpdate,
    setIdUser,
    dataFilterUsers,
    setDataFilterUsers,
    dataFilterEvents,
    setDataFilterEvents,
    tabStaff,
    setTabStaff,
    events,
    setEvents,
    updateStaff,
  } = useStaffStore();
  const { searchTerm, setSearchTerm } = useUtilsStore();
  const { isLoading, setIsLoading } = useLoaderStore();

  // Hooks de tri pour chaque type de données
  const [sortedEvents, handleSortEvents, eventsSortField, eventsSortDirection] =
    useSorting(dataFilterEvents);

  // Permet de savoir si on est en train de créer ou de modifier
  const isCreatingOrUpdating = createButton || viewOrUpdate;

  useEffect(() => {
    if (!isCreatingOrUpdating && tabStaff === "Utilisateurs") {
      setIdUser(0);
      setIsLoading(true);
      getUsers()
        .then((res) => {
          // Trie : d'abord ceux avec leavingDate === null, puis par ordre alphabétique du nom
          const sortedData = res.data.sort((a: any, b: any) => {
            // Les utilisateurs actifs (leavingDate === null) en premier
            if (a.leavingDate === null && b.leavingDate !== null) return -1;
            if (a.leavingDate !== null && b.leavingDate === null) return 1;

            // Si les deux ont le même statut, trier par nom
            return a.lastName.toString().localeCompare(b.lastName.toString());
          });
          setUsers(sortedData);
          setIsLoading(false);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
          setIsLoading(false);
        });
    } else if (!isCreatingOrUpdating && tabStaff === "Évènements") {
      setIsLoading(true);
      getEvents()
        .then((res) => {
          const sortedData = res.data.sort((a: any, b: any) => {
            return b.startDate.localeCompare(a.startDate);
          });
          setEvents(sortedData);
          setIsLoading(false);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
          setIsLoading(false);
        });
    }
  }, [createButton, viewOrUpdate, tabStaff, updateStaff]);

  // Gestion de la recherche
  useEffect(() => {
    if (tabStaff === "Évènements") {
      let data = events?.length > 0 ? [...events] : [...dataFilterEvents];
      if (searchTerm !== "") {
        data = data.filter(
          (item) =>
            item["user"]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item["nameTimeLabel"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["startDate"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["endDate"]?.toLowerCase().includes(searchTerm.toLowerCase()),
        );

        setDataFilterEvents(data);
      } else {
        setDataFilterEvents(events);
      }
    } else {
      let data = users?.length > 0 ? [...users] : [...dataFilterUsers];

      if (searchTerm !== "") {
        data = data.filter(
          (item) =>
            item["firstName"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["lastName"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["role"]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item["departments"]
              ?.map((department: any) => department.name)
              .join(" ")
              .toLowerCase()
              .includes(searchTerm.toLowerCase()),
        );
      }
      setDataFilterUsers(data);
    }
  }, [searchTerm, users, events]);

  const handleClick = () => {
    if (tabStaff === "Utilisateurs") setTabStaff("Évènements");
    else setTabStaff("Utilisateurs");
  };

  const isUsers = tabStaff === "Utilisateurs";
  const activeData = isUsers ? dataFilterUsers : sortedEvents;
  const rowCount = activeData?.length ?? 0;

  return (
    <PageShell>
      {isCreatingOrUpdating ? (
        <PagePanel>{isUsers ? <User /> : <Events />}</PagePanel>
      ) : (
        <>
          <PageHeader
            eyebrow="Staff"
            title={isUsers ? "Utilisateurs" : "Évènements"}
            description={
              isUsers
                ? "Collaborateurs, services et rôles."
                : "Absences et évènements déclarés par les collaborateurs."
            }
            actions={
              <ActionButton
                variant="primary"
                icon={<PlusIcon />}
                onClick={() => setCreateButton(!createButton)}
              >
                {isUsers ? "Ajouter un utilisateur" : "Ajouter un évènement"}
              </ActionButton>
            }
          />

          <PagePanel>
            <PanelToolbar>
              <ToolbarRow>
                <TabBar
                  tabs={[
                    { id: "Utilisateurs", label: "Utilisateurs" },
                    { id: "Évènements", label: "Évènements" },
                  ]}
                  value={tabStaff}
                  onChange={(id) => {
                    if (id !== tabStaff) handleClick();
                  }}
                  className="sm:w-80"
                />
                <ResultCount
                  count={rowCount}
                  singular={isUsers ? "utilisateur" : "évènement"}
                  plural={isUsers ? "utilisateurs" : "évènements"}
                  filtered={searchTerm !== ""}
                />
              </ToolbarRow>

              <ToolbarRow>
                <SearchField
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder={
                    isUsers
                      ? "Rechercher un nom, un service, un rôle…"
                      : "Rechercher un utilisateur, un motif…"
                  }
                />

                {/* Filtres et tri */}
                {tabStaff === "Évènements" && (
                  <SortBar>
                    <button
                      onClick={() => handleSortEvents("user")}
                      className={getSortButtonClasses("user", eventsSortField)}
                    >
                      <span>Utilisateur</span>
                      {getSortIcon("user", eventsSortField, eventsSortDirection)}
                    </button>
                    <button
                      onClick={() => handleSortEvents("startDate")}
                      className={getSortButtonClasses(
                        "startDate",
                        eventsSortField,
                      )}
                    >
                      <span>Date</span>
                      {getSortIcon(
                        "startDate",
                        eventsSortField,
                        eventsSortDirection,
                      )}
                    </button>
                    <button
                      onClick={() => handleSortEvents("endDate")}
                      className={getSortButtonClasses(
                        "endDate",
                        eventsSortField,
                      )}
                    >
                      <span>Date de fin</span>
                      {getSortIcon(
                        "endDate",
                        eventsSortField,
                        eventsSortDirection,
                      )}
                    </button>
                  </SortBar>
                )}
              </ToolbarRow>
            </PanelToolbar>

            {/* Contenu du tableau */}
            {isLoading ? (
              <PanelCenter>
                <LoadingSpinner size="large" text="Chargement des données..." />
              </PanelCenter>
            ) : isUsers ? (
              <TableStaff
                columns={[
                  { header: "Nom", accessor: "lastName" },
                  { header: "Prénom", accessor: "firstName" },
                  { header: "Services", accessor: "departments" },
                  { header: "Rôle", accessor: "role" },
                ]}
                data={dataFilterUsers}
              />
            ) : (
              <TableStaff
                columns={[
                  { header: "Utilisateur", accessor: "user" },
                  { header: "Motif", accessor: "nameTimeLabel" },
                  { header: "Date de début", accessor: "startDate" },
                  { header: "Date de fin", accessor: "endDate" },
                  { header: "Commentaire", accessor: "description" },
                ]}
                data={sortedEvents}
              />
            )}
          </PagePanel>
        </>
      )}
    </PageShell>
  );
};

export default Staff;
